import { useLocation } from 'react-router';

import type { PrefetchContext, PrefetchedDataProps } from '#src/shared/prefetch';

import { getPsfFirstStepRoute } from '#src/app/pages/psf/promoteOrShare/shared/utils';
import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';
import { useQueryString } from '#src/app/shared/hooks/location';
import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';
import { getQueryStringSingleValue } from '#src/app/shared/utils/query';

import { prefetchPsfPromoteOrSharePageDataFromApi, usePsfPromoteOrSharePageDataFromApi } from './pageData';

export type PsfPromoteOrSharePagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof usePsfPromoteOrSharePageDataFromApi>[0]>;
}>;

export type PsfPromoteOrSharePageCombinedState = MergedState<ReturnType<typeof usePsfPromoteOrSharePageDataFromApi>>;

export function usePageContext(
	slug: string,
	prefetchedData?: PsfPromoteOrSharePagePrefetchedContext,
): PsfPromoteOrSharePageCombinedState {
	const query = useQueryString();
	const countryCode = useCountryCode();
	const locale = useLocale();
	const { pathname } = useLocation();
	const route = getPsfFirstStepRoute(pathname);

	return useMergedStates(
		usePsfPromoteOrSharePageDataFromApi(
			prefetchedData?.pageData,
			{ slug, locale, countryCode, route, query },
			{
				recruiterId: getQueryStringSingleValue(query, 'recruiter'),
			},
		),
	);
}

export async function prefetchPageContext(
	slug: string,
	context: PrefetchContext,
): Promise<PsfPromoteOrSharePagePrefetchedContext> {
	const {
		l10n: { countryCode, locale },
		path,
		query,
	} = context;
	const route = getPsfFirstStepRoute(path);
	const [pageData] = await Promise.all([
		prefetchPsfPromoteOrSharePageDataFromApi(
			context,
			{ slug, locale, countryCode, route, query },
			{
				recruiterId: getQueryStringSingleValue(context.query, 'recruiter'),
			},
		),
	]);

	return { pageData };
}

export type PsfPromoteOrSharePagePrefetchedDataProps = PrefetchedDataProps<PsfPromoteOrSharePagePrefetchedContext>;
